import { createContext } from 'react';
import {
	TFileId,
	IItemForGetShortInfo,
	IItemForGetShortInfoWithStatus,
	IServerError,
	ICreateDishValidationError,
	TDishValidationType,
	TLanguageIDs,
	IMenuTranslation,
	IDefaultLanguage,
	ITotalPrice,
	IItemForGetShortDishInfo,
} from 'types';

export type TCreateDishVisibleForm = 'mainForm' | 'translations';
export type TTypeOfCreateDishModalWindow =
	| null
	| 'toConfirmFieldsClearing'
	| 'success'
	| 'error'
	| 'defaultTranslationIsEmpty';
export interface ICreateDishErrorResponseData extends IServerError<ICreateDishValidationError> {
	errorType: TDishValidationType;
}
export interface IIDsShowInComboDishes {
	[key: string]: boolean; // key - is the ID and boolean means showInCombo or hiddenFromCombo status.
}

export interface ICreateDishState {
	visibleForm: TCreateDishVisibleForm;
	fileId: TFileId;
	name: string;
	fetchedCustomers: IItemForGetShortInfo[];
	selectedCustomer: Nullable<string>;
	fetchedRestaurants: IItemForGetShortInfoWithStatus[];
	restaurantsForPrice: string[];
	price: string;
	totalPrices: ITotalPrice[];
	restaurantWithTurnOffComment: string[];
	restaurantWithRequiredComment: string[];
	shortDescription: string;
	isDisabledInRestaurants: string[];
	defaultCustomerLanguage: Nullable<IDefaultLanguage>;
	selectedTranslationTab: TLanguageIDs | null;
	translations: IMenuTranslation[];
	activationSchedule: string;
	deactivationSchedule: string;
	fetchedCategories: IItemForGetShortInfo[];
	selectedSuggestedDishCategory: Nullable<string>;
	fetchedSuggestedDishSubCategories: IItemForGetShortInfo[];
	selectedSuggestedDishSubCategory: Nullable<string>;
	fetchedSuggestedDishes: IItemForGetShortDishInfo[];
	selectedSuggestedDishes: string[];
	selectedAdditionalDishCategory: Nullable<string>;
	fetchedAdditionalDishSubCategories: IItemForGetShortInfo[];
	selectedAdditionalDishSubCategory: Nullable<string>;
	fetchedAdditionalDishes: IItemForGetShortDishInfo[];
	selectedAdditionalDishes: string[];
	selectedDishModificationCategory: Nullable<string>;
	fetchedDishModificationSubCategories: IItemForGetShortInfo[];
	selectedDishModificationSubCategory: Nullable<string>;
	fetchedDishModifications: IItemForGetShortDishInfo[];
	selectedDishModifications: string[];
	IDsShowInComboDishes: IIDsShowInComboDishes;
	externalId: Nullable<string>;
	typeOfModalWindow: TTypeOfCreateDishModalWindow;
	errorResponseData: ICreateDishErrorResponseData | null;
}

export const createDishInitialState: ICreateDishState = {
	visibleForm: 'mainForm',
	fileId: null,
	name: '',
	fetchedCustomers: [],
	selectedCustomer: null,
	fetchedRestaurants: [],
	restaurantsForPrice: [],
	price: '',
	totalPrices: [],
	restaurantWithTurnOffComment: [],
	restaurantWithRequiredComment: [],
	shortDescription: '',
	isDisabledInRestaurants: [],
	defaultCustomerLanguage: null,
	selectedTranslationTab: null,
	translations: [],
	activationSchedule: '',
	deactivationSchedule: '',
	fetchedCategories: [],
	selectedSuggestedDishCategory: null,
	fetchedSuggestedDishSubCategories: [],
	selectedSuggestedDishSubCategory: null,
	fetchedSuggestedDishes: [],
	selectedSuggestedDishes: [],
	selectedAdditionalDishCategory: null,
	fetchedAdditionalDishSubCategories: [],
	selectedAdditionalDishSubCategory: null,
	fetchedAdditionalDishes: [],
	selectedAdditionalDishes: [],
	selectedDishModificationCategory: null,
	fetchedDishModificationSubCategories: [],
	selectedDishModificationSubCategory: null,
	fetchedDishModifications: [],
	selectedDishModifications: [],
	IDsShowInComboDishes: {},
	externalId: null,
	typeOfModalWindow: null,
	errorResponseData: null,
};

export interface ICreateDishContext extends ICreateDishState {
	changeVisibleForm(value: TCreateDishVisibleForm): void;
	setFileId(value: TFileId): void;
	changeDishName(value: string): void;
	setFetchedCustomers(value: IItemForGetShortInfo[]): void;
	setCustomer(value: Nullable<string>): void;
	setFetchedRestaurants(value: IItemForGetShortInfoWithStatus[]): void;
	setRestaurantsForPrice(value: string[]): void;
	changePrice(value: string): void;
	addPrices(value: ITotalPrice[]): void;
	removePrice(value: string[]): void;
	setRestaurantWithTurnOffComment(value: string[]): void;
	setRestaurantWithRequiredComment(value: string[]): void;
	changeShortDescription(value: string): void;
	setDisabledInRestaurants(value: string[]): void;
	setFetchedTranslations(value: TLanguageIDs[], defaultCustomerLanguage: Nullable<IDefaultLanguage>): void;
	setTranslationTab(value: TLanguageIDs | null): void;
	changeNameTranslation(languageId: TLanguageIDs, value: string): void;
	changeDescriptionTranslation(languageId: TLanguageIDs, value: string): void;
	changeActivationSchedule(value: string): void;
	changeDeactivationSchedule(value: string): void;
	setFetchedCategories(value: IItemForGetShortInfo[]): void;
	setSuggestedDishCategory(value: Nullable<string>): void;
	setFetchedSuggestedDishSubCategories(value: IItemForGetShortInfo[]): void;
	setSuggestedDishSubCategory(value: Nullable<string>): void;
	setFetchedSuggestedDishes(value: IItemForGetShortInfo[]): void;
	setSuggestedDishes(value: string[]): void;
	removeSuggestedDish(value: string): void;
	setAdditionalDishCategory(value: Nullable<string>): void;
	setFetchedAdditionalDishSubCategories(value: IItemForGetShortInfo[]): void;
	setAdditionalDishSubCategory(value: Nullable<string>): void;
	setFetchedAdditionalDishes(value: IItemForGetShortInfo[]): void;
	setAdditionalDishes(value: string[]): void;
	removeAdditionalDish(value: string): void;
	setDishModificationCategory(value: Nullable<string>): void;
	setFetchedDishModificationSubCategories(value: IItemForGetShortInfo[]): void;
	setDishModificationSubCategory(value: Nullable<string>): void;
	setFetchedDishModifications(value: IItemForGetShortInfo[]): void;
	setDishModifications(value: string[]): void;
	removeDishModification(value: string): void;
	setIDsShowInComboDishes(value: IIDsShowInComboDishes): void;
	changeExternalId(value: string): void;
	generateUniqueExternalId(): void;
	changeTypeOfModalWindow(value: TTypeOfCreateDishModalWindow): void;
	showErrorModalWithInfo(value: ICreateDishErrorResponseData | null): void;
	resetFields(): void;
}

export const CreateDishContext = createContext<ICreateDishContext>({
	changeVisibleForm: () => undefined,
	setFileId: () => undefined,
	changeDishName: () => undefined,
	setFetchedCustomers: () => undefined,
	setCustomer: () => undefined,
	setFetchedRestaurants: () => undefined,
	setRestaurantsForPrice: () => undefined,
	changePrice: () => undefined,
	addPrices: () => undefined,
	removePrice: () => undefined,
	setRestaurantWithTurnOffComment: () => undefined,
	setRestaurantWithRequiredComment: () => undefined,
	changeShortDescription: () => undefined,
	setDisabledInRestaurants: () => undefined,
	setFetchedTranslations: () => undefined,
	setTranslationTab: () => undefined,
	changeNameTranslation: () => undefined,
	changeDescriptionTranslation: () => undefined,
	changeActivationSchedule: () => undefined,
	changeDeactivationSchedule: () => undefined,
	setFetchedCategories: () => undefined,
	setSuggestedDishCategory: () => undefined,
	setFetchedSuggestedDishSubCategories: () => undefined,
	setSuggestedDishSubCategory: () => undefined,
	setFetchedSuggestedDishes: () => undefined,
	setSuggestedDishes: () => undefined,
	removeSuggestedDish: () => undefined,
	setAdditionalDishCategory: () => undefined,
	setFetchedAdditionalDishSubCategories: () => undefined,
	setAdditionalDishSubCategory: () => undefined,
	setFetchedAdditionalDishes: () => undefined,
	setAdditionalDishes: () => undefined,
	removeAdditionalDish: () => undefined,
	setDishModificationCategory: () => undefined,
	setFetchedDishModificationSubCategories: () => undefined,
	setDishModificationSubCategory: () => undefined,
	setFetchedDishModifications: () => undefined,
	setDishModifications: () => undefined,
	removeDishModification: () => undefined,
	setIDsShowInComboDishes: () => undefined,
	changeExternalId: () => undefined,
	generateUniqueExternalId: () => undefined,
	changeTypeOfModalWindow: () => undefined,
	showErrorModalWithInfo: () => undefined,
	resetFields: () => undefined,
	...createDishInitialState,
});
